import { Loader2, UserMinus, UserPlus } from "lucide-react";
import { useState } from "react";

import { adminService } from "@/api/admin/admin.service";
import type { TAdminUser, TRoleUser } from "@/api/admin/admin.types";
import { UserPicker } from "@/components/admin";
import { DataGrid, type TColumnDef, type TDataGridQuery } from "@/components/data-grid";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";

const PAGE_SIZE = 20;
const SALES_ROLE = "sales";

/**
 * Sales team management (RD-3, amended 2026-07-30): a Reviewers-style page for
 * the `sales` role. Membership matters operationally — everyone holding the
 * role at send time receives the new-onboarding-job notification emails, and
 * revoking takes effect on the member's next request.
 */
export function SalesTeamPage() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);

  const [selectedUser, setSelectedUser] = useState<TAdminUser | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<TRoleUser | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const reload = () => setReloadTick((t) => t + 1);

  const fetcher = async (query: TDataGridQuery) => {
    const rolesResponse = await adminService.getRoles({ signal: query.signal });
    if (rolesResponse.error) throw rolesResponse.error;
    const salesRole = rolesResponse.data?.data.find((role) => role.name === SALES_ROLE);
    if (!salesRole) {
      // Role not seeded yet — show an empty grid rather than erroring.
      return { items: [], total_count: 0, limit: PAGE_SIZE, offset: 0 };
    }
    const { data, error } = await adminService.getRoleUsers(salesRole.role_id, undefined, { signal: query.signal });
    if (error) throw error;
    return data?.data;
  };

  const handleAssign = async () => {
    if (!selectedUser) {
      toast({ variant: "destructive", title: "Select a user first" });
      return;
    }
    setIsAssigning(true);
    const { error } = await adminService.assignRole(selectedUser.id, SALES_ROLE);
    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to assign sales role",
        description: await extractErrorMessage(error),
      });
    } else {
      toast({
        title: "Sales role assigned",
        description: "They will now receive new investor onboarding job notifications.",
      });
      setSelectedUser(null);
      reload();
    }
    setIsAssigning(false);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    const { error } = await adminService.revokeRole(revokeTarget.id, SALES_ROLE);
    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to revoke sales role",
        description: await extractErrorMessage(error),
      });
    } else {
      toast({
        title: "Sales role revoked",
        description: "They lose Job Pool access on their next request and stop receiving notifications.",
      });
      reload();
    }
    setIsRevoking(false);
    setRevokeTarget(null);
  };

  const fullName = (member: TRoleUser) => `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim();

  const columns: TColumnDef<TRoleUser>[] = [
    {
      id: "name",
      header: "Name",
      sortable: true,
      accessor: (member) => fullName(member).toLowerCase(),
      cell: (member) => <span className="font-medium text-foreground">{fullName(member) || "—"}</span>,
    },
    {
      id: "email",
      header: "Email",
      type: "email",
      sortable: true,
      accessor: (member) => member.email ?? "—",
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Sales</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage the sales team — members vet registering investors and receive new onboarding job notifications
            </p>
          </div>
        </div>

        {/* Assign sales role */}
        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">Assign sales role</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="max-w-md flex-1">
              <UserPicker
                value={selectedUser}
                onChange={setSelectedUser}
              />
            </div>
            <Button
              onClick={() => void handleAssign()}
              disabled={isAssigning || !selectedUser}
            >
              {isAssigning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Assigning…
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign
                </>
              )}
            </Button>
          </div>
        </div>

        <DataGrid<TRoleUser>
          getRowId={(row) => row.id}
          persistKey="admin.salesTeam"
          ariaLabel="Sales team members"
          data={{
            mode: "client",
            fetcher,
            deps: [reloadTick],
            searchFields: ["first_name", "last_name", "email"],
          }}
          columns={columns}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search by name or email",
          }}
          rowActions={(member) => [
            {
              label: "Revoke sales role",
              icon: <UserMinus className="h-4 w-4" />,
              variant: "destructive",
              onClick: () => setRevokeTarget(member),
            },
          ]}
          emptyState={{
            title: "No sales team members yet.",
            description: "Assign the sales role to someone so new investor onboarding jobs reach a human.",
          }}
        />

        <ConfirmDialog
          open={revokeTarget !== null}
          onOpenChange={(open) => !open && setRevokeTarget(null)}
          title="Revoke the sales role?"
          description={
            revokeTarget
              ? `${fullName(revokeTarget) || revokeTarget.email} will lose access to the Investor Onboarding Job Pool immediately and stop receiving job notifications.`
              : undefined
          }
          confirmLabel="Revoke"
          isConfirming={isRevoking}
          onConfirm={handleRevoke}
        />
      </div>
    </div>
  );
}
